<template>
  <div class="container-fluid">
    <div class="row justify-content-md-center">
      <div class="col-lg-8">
        <div class="h2 text-dark">{{ labels.title }}</div>
        <b-card no-body>
          <b-tabs content-class="mt-3" card>

            <!-- Users tab -->
            <b-tab :title="labels.tabs.users">
              <div class="text-muted">{{ labels.tabs.usersPlaceholder }}</div>
            </b-tab>

            <!-- Popup tab -->
            <b-tab :title="labels.tabs.popup" @click="onPopupTabClick">

              <!-- Document list view -->
              <div v-if="!selectedDoc">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="h5 mb-0">{{ labels.popup.docsTitle }}</div>
                  <b-btn size="sm" variant="outline-secondary" @click="loadDocList" :disabled="listLoading">
                    <span class="mdi mdi-refresh"></span>
                  </b-btn>
                </div>

                <div v-if="listLoading" class="text-muted">{{ labels.popup.loading }}</div>
                <div v-else-if="listError" class="alert alert-danger">{{ listError }}</div>
                <div v-else>
                  <b-list-group class="mb-3">
                    <b-list-group-item
                      v-for="doc in docList"
                      :key="doc._id"
                      class="d-flex justify-content-between align-items-center"
                      button
                      @click="selectDoc(doc)"
                    >
                      <div>
                        <span class="mdi mdi-file-document-outline mr-2 text-muted"></span>
                        <strong>{{ doc._id }}</strong>
                        <span class="text-muted ml-2 small" v-if="doc._attachments">
                          {{ Object.keys(doc._attachments).length }} {{ labels.popup.attachmentCount }}
                        </span>
                      </div>
                      <span class="mdi mdi-chevron-right text-muted"></span>
                    </b-list-group-item>
                  </b-list-group>

                  <div v-if="docList.length === 0" class="text-muted mb-3">{{ labels.popup.noDocs }}</div>

                  <!-- Create new document -->
                  <div class="border-top pt-3">
                    <div class="h6">{{ labels.popup.newDocTitle }}</div>
                    <div class="d-flex">
                      <b-form-input
                        v-model="newDocId"
                        :placeholder="labels.popup.newDocPlaceholder"
                        class="mr-2"
                        size="sm"
                      ></b-form-input>
                      <b-btn size="sm" variant="primary" @click="createDoc" :disabled="!newDocId.trim() || creating">
                        <span class="mdi mdi-plus"></span> {{ labels.popup.create }}
                      </b-btn>
                    </div>
                    <div v-if="createError" class="alert alert-danger mt-2">{{ createError }}</div>
                  </div>
                </div>
              </div>

              <!-- Document detail view -->
              <div v-else>
                <div class="d-flex align-items-center mb-3">
                  <b-btn size="sm" variant="outline-secondary" class="mr-3" @click="backToList">
                    <span class="mdi mdi-arrow-left"></span> {{ labels.popup.back }}
                  </b-btn>
                  <div class="h5 mb-0">
                    <span class="mdi mdi-file-document-outline mr-1 text-muted"></span>
                    {{ selectedDoc._id }}
                  </div>
                </div>

                <div v-if="detailError" class="alert alert-danger">{{ detailError }}</div>

                <!-- Attachments list -->
                <div class="h6">{{ labels.popup.attachmentsTitle }}</div>
                <div v-if="!hasAttachments" class="text-muted mb-3">{{ labels.popup.noAttachments }}</div>
                <b-list-group v-else class="mb-4">
                  <b-list-group-item
                    v-for="(att, name) in selectedDoc._attachments"
                    :key="name"
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <span class="mdi mdi-paperclip mr-2"></span>
                      <strong>{{ name }}</strong>
                      <span class="text-muted ml-2 small">{{ att.content_type }}</span>
                      <span class="text-muted ml-2 small">{{ humanSize(att.length) }}</span>
                    </div>
                    <div>
                      <b-btn size="sm" variant="info" class="mr-1" @click="viewAttachment(name)">
                        <span class="mdi mdi-eye"></span> {{ labels.popup.view }}
                      </b-btn>
                      <b-btn size="sm" variant="secondary" class="mr-1" @click="downloadAttachment(name)">
                        <span class="mdi mdi-download"></span> {{ labels.popup.download }}
                      </b-btn>
                      <b-btn size="sm" variant="danger" @click="deleteAttachment(name)">
                        <span class="mdi mdi-delete"></span>
                      </b-btn>
                    </div>
                  </b-list-group-item>
                </b-list-group>

                <!-- Add attachment -->
                <div class="border-top pt-3">
                  <div class="h6">{{ labels.popup.addAttachment }}</div>
                  <div class="d-flex align-items-center">
                    <b-form-file
                      v-model="newFile"
                      class="mr-2"
                      :placeholder="labels.popup.choosefile"
                    ></b-form-file>
                    <b-btn variant="primary" @click="uploadAttachment" :disabled="!newFile || uploading">
                      <span v-if="uploading">
                        <span class="mdi mdi-loading mdi-spin"></span> {{ labels.popup.uploading }}
                      </span>
                      <span v-else>
                        <span class="mdi mdi-upload"></span> {{ labels.popup.upload }}
                      </span>
                    </b-btn>
                  </div>
                  <div v-if="uploadSuccess" class="alert alert-success mt-2">{{ labels.popup.uploadSuccess }}</div>
                </div>
              </div>

            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
var filesize = require('file-size')

export default {
  name: 'systemAdmin',
  data() {
    return {
      // list view
      listLoading: false,
      listError: null,
      docList: [],
      newDocId: '',
      creating: false,
      createError: null,
      // detail view
      selectedDoc: null,
      detailError: null,
      newFile: null,
      uploading: false,
      uploadSuccess: false,
    }
  },
  computed: {
    labels() { return this.$store.state.labels.admin.systemAdmin },
    db() { return this.$store.state.dbDatabaseSystem },
    hasAttachments() {
      return this.selectedDoc && this.selectedDoc._attachments &&
        Object.keys(this.selectedDoc._attachments).length > 0
    },
  },
  mounted() {
    this.loadDocList()
  },
  methods: {
    humanSize(bytes) {
      if (!bytes) return '0 B'
      return filesize(bytes, { fixed: 1 }).human('si')
    },
    onPopupTabClick() {
      if (!this.selectedDoc) this.loadDocList()
    },
    loadDocList() {
      this.listLoading = true
      this.listError = null
      this.db.allDocs({ include_docs: true }).then(result => {
        this.docList = result.rows
          .map(r => r.doc)
          .filter(doc => !doc._id.startsWith('_design/'))
        this.listLoading = false
      }).catch(err => {
        this.listError = err.message || err
        this.listLoading = false
      })
    },
    selectDoc(doc) {
      this.selectedDoc = doc
      this.detailError = null
      this.uploadSuccess = false
      this.newFile = null
    },
    backToList() {
      this.selectedDoc = null
      this.loadDocList()
    },
    createDoc() {
      const id = this.newDocId.trim()
      if (!id) return
      this.creating = true
      this.createError = null
      this.db.put({ _id: id }).then(() => {
        this.newDocId = ''
        this.creating = false
        this.loadDocList()
      }).catch(err => {
        this.creating = false
        this.createError = err.message || err
      })
    },
    refreshSelectedDoc() {
      this.db.get(this.selectedDoc._id).then(doc => {
        this.selectedDoc = doc
      }).catch(err => {
        this.detailError = err.message || err
      })
    },
    viewAttachment(name) {
      this.db.getAttachment(this.selectedDoc._id, name).then(blob => {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
      }).catch(err => {
        this.detailError = err.message || err
      })
    },
    downloadAttachment(name) {
      this.db.getAttachment(this.selectedDoc._id, name).then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = name
        a.click()
        URL.revokeObjectURL(url)
      }).catch(err => {
        this.detailError = err.message || err
      })
    },
    deleteAttachment(name) {
      this.db.removeAttachment(this.selectedDoc._id, name, this.selectedDoc._rev).then(() => {
        this.refreshSelectedDoc()
      }).catch(err => {
        this.detailError = err.message || err
      })
    },
    uploadAttachment() {
      if (!this.newFile) return
      this.uploading = true
      this.uploadSuccess = false
      this.detailError = null
      this.db.putAttachment(this.selectedDoc._id, this.newFile.name, this.selectedDoc._rev, this.newFile, this.newFile.type)
        .then(() => {
          this.uploading = false
          this.uploadSuccess = true
          this.newFile = null
          this.refreshSelectedDoc()
        }).catch(err => {
          this.uploading = false
          this.detailError = err.message || err
        })
    },
  },
}
</script>
